import { IconS24DocumentText24, IconS24Letter24, IconS24Telephone24 } from '@is24/cosma-ui-icons';
import { Font } from 'is24-corecss';
import { useMemo } from 'react';
import { AragAddressDetails } from './AragAddressDetails';
import { AragUserDetailsSection } from './AragUserDetailsSection';
import { RenderSection } from './OverviewTab';
import styles from './styles';
import VersicherungsausweisS from '../pdfs/Versicherungspaket_S.pdf';
import VersicherungsausweisM from '../pdfs/Versicherungspaket_M.pdf';
import VersicherungsausweisL from '../pdfs/Versicherungspaket_L.pdf';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useGetInsuranceDetails } from '@/apiHooks/insurance.hook';
import { useGetProductsStatus } from '@/apiHooks/productsStatus.hook';
import { InsuranceServiceName } from '@/@types/insurance';
import { createTelLinkHandler } from '@/utils/telLinkUtils';

export const AragSection = ({ serviceName }: { serviceName: InsuranceServiceName }) => {
  const { t } = useIntl();
  const { insuranceDetails } = useGetInsuranceDetails(true);
  const { productsStatus } = useGetProductsStatus();
  const serviceNamelowerCase = serviceName.toLowerCase();

  const packageType = useMemo(() => {
    return productsStatus?.insurancePackage?.type?.toLowerCase() ?? 's';
  }, [productsStatus]);

  const insuranceCertificates = {
    s: VersicherungsausweisS,
    m: VersicherungsausweisM,
    l: VersicherungsausweisL,
  };
  const insuranceCertificate =
    insuranceCertificates[packageType as keyof typeof insuranceCertificates] || VersicherungsausweisS;

  return (
    <>
      <div className='product-selection-card-container margin-bottom-l margin-top-l'>
        <div className='product-selection-card'>
          <div className='product-content'></div>
          <RenderSection
            title={t({
              id: `common.aragModal.form.customerNumber`,
              defaultMessage: 'Deine Kundennummer',
            })}
            content={<p>{insuranceDetails?.customerNumber}</p>}
          />
          <RenderSection
            title={t({
              id: `common.aragModal.form.policyNumber`,
              defaultMessage: 'Deine Versicherungsnummer',
            })}
            content={<p>{insuranceDetails?.insuranceNumber}</p>}
          />
          <AragUserDetailsSection serviceName={serviceName} />
          <RenderSection
            title={t({
              id: `common.aragModal.insuranceDetails.contact.key`,
              defaultMessage: 'Kontaktdaten für den Notfall',
            })}
            content={
              <>
                <a
                  className={`${styles.iconLink}`}
                  href={`tel:${t({
                    id: `common.aragModal.insuranceDetails.contact.phoneNumber.${serviceNamelowerCase}`,
                    defaultMessage: '0221 80247 1360',
                  })}`}
                  onClick={createTelLinkHandler(
                    t({
                      id: `common.aragModal.insuranceDetails.contact.phoneNumber.${serviceNamelowerCase}`,
                      defaultMessage: '0221 80247 1360',
                    }),
                  )}
                >
                  <IconS24Telephone24 height={16} width={16} className='padding-right-xs' />
                  {t({
                    id: `common.aragModal.insuranceDetails.contact.phoneNumber.${serviceNamelowerCase}`,
                    defaultMessage: '0221 80247 1360',
                  })}
                </a>
                <Font className={`margin-bottom-m ${styles.lightgray}`}>
                  {t({
                    id: `common.aragModal.insuranceDetails.contact.availability`,
                    defaultMessage: 'Täglich rund um die Uhr erreichbar',
                  })}
                </Font>
                {[InsuranceServiceName.MEDIATION, InsuranceServiceName.MIETRECHTSSCHUTZ].includes(serviceName) && (
                  <a
                    className={`${styles.iconLink}`}
                    href={`mailto:${t({
                      id: `common.aragModal.insuranceDetails.contact.eMail`,
                      defaultMessage: 'service@arag.de',
                    })}`}
                  >
                    <IconS24Letter24 height={16} width={16} className='padding-right-xs' />
                    {t({
                      id: `common.aragModal.insuranceDetails.contact.eMail`,
                      defaultMessage: 'service@arag.de',
                    })}
                  </a>
                )}
              </>
            }
          />
          <RenderSection
            title={t({
              id: `common.aragModal.insuranceDetails.documents.key`,
              defaultMessage: 'Dokumente',
            })}
            content={
              <>
                <a
                  className='margin-top-xs display-flex grid-align-start'
                  href={insuranceCertificate}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <IconS24DocumentText24 height={16} width={16} className='padding-right-xs' />
                  <span>
                    {t({
                      id: `common.aragModal.insuranceDetails.documents.certificate`,
                      defaultMessage: 'Versicherungsausweiß',
                    })}
                  </span>
                </a>
                <a
                  className='margin-top-xs display-flex grid-align-start'
                  href={t({
                    id: `common.aragModal.insuranceDetails.documents.link.${packageType}`,
                    defaultMessage: 'Dokumente',
                  })}
                  target='_blank'
                >
                  <IconS24DocumentText24 height={16} width={16} className='padding-right-xs' />
                  <span>
                    {t({
                      id: `common.aragModal.insuranceDetails.documents.conditions`,
                      defaultMessage: 'Versicherungsbedingungen',
                    })}
                  </span>
                </a>
              </>
            }
          />
        </div>
      </div>
      <AragAddressDetails />
    </>
  );
};
